import type { Artifact } from '@features/applications/applications.models';
import { buildDockerImageHref, parseDockerImageReference } from '@utils/docker';

/**
 * Builds the client-side path to the image analysis view for a given image
 * artifact reference. Delegates to {@link parseDockerImageReference} for
 * parsing and {@link buildDockerImageHref} for path construction.
 *
 * Returns `null` when the reference cannot be parsed or essential parts
 * (registry, repository, tag) are missing.
 */
export const getImageAnalysisPath = ({
  digest,
  reference,
}: {
  digest: string;
  reference: string;
}): string | null => {
  const parsed = parseDockerImageReference(reference);

  if (!parsed) return null;

  return buildDockerImageHref({ ...parsed, digest });
};

/**
 * Returns the first artifact from an entity's artifact list, representing
 * the "primary" artifact. Returns `undefined` when no artifacts exist.
 *
 * Accepts any object whose `artifacts` field matches the shape used by both
 * `Asset` and `PackageAsset`.
 */
export const getPrimaryArtifact = (
  entity: { artifacts?: { items?: Artifact[] } } | undefined,
): Artifact | undefined => entity?.artifacts?.items?.[0];
